import { ChevronRight } from '@/assets/icons'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { StorageReference } from 'firebase/storage'
import clsx from 'clsx'

export type BreadcrumbItem = {
  label: string
  storageRef: StorageReference
}

type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbItem[]
  onBreadcrumbClick: (storageRef: StorageReference) => void
  rootLabel?: string
}

export function Breadcrumbs({ breadcrumbs, onBreadcrumbClick, rootLabel = 'My Drive' }: BreadcrumbsProps) {
  const deviceType = useDeviceType()
  const shouldTruncate = deviceType !== DEVICE_TYPE.DESKTOP ? breadcrumbs.length >= 2 : breadcrumbs.length >= 5

  if (breadcrumbs.length === 0) return null

  const last = breadcrumbs[breadcrumbs.length - 1]
  const hidden = breadcrumbs.slice(0, -1)

  return (
    <div className="inline-flex items-center overflow-hidden max-w-full">
      {shouldTruncate ? (
        <DropdownMenu.Root>
          <div className="inline-flex items-center">
            <DropdownMenu.Trigger className="text-sm underline cursor-pointer text-nowrap text-brand-neutral-600 text-ellipsis px-2 py-1.5 rounded">
              {rootLabel}...
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="z-[999] rounded-md border border-brand-neutral-200 bg-white p-2 text-sm shadow-lg max-w-[260px]">
                {hidden.map((item) => (
                  <DropdownMenu.Item
                    key={item.storageRef.fullPath}
                    className="flex items-center px-3 py-2 cursor-pointer rounded text-brand-neutral-600 font-semibold hover:bg-brand-neutral-100 hover:text-brand-neutral-600 focus:outline-none focus:bg-brand-neutral-100 focus:text-brand-neutral-950"
                    onClick={() => onBreadcrumbClick(item.storageRef)}
                  >
                    <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
            <ChevronRight className="text-brand-neutral-500 w-5 h-5" />
            <span
              className={clsx(
                'font-semibold text-sm text-brand-neutral-950 cursor-default text-nowrap overflow-hidden text-ellipsis whitespace-nowrap',
                deviceType !== DEVICE_TYPE.DESKTOP && 'max-w-[160px]'
              )}
              title={last.label}
            >
              {last.label}
            </span>
          </div>
        </DropdownMenu.Root>
      ) : (
        <div className="inline-flex items-center overflow-hidden max-w-full">
          {breadcrumbs.map((breadcrumb, index) => (
            <div key={index} className="inline-flex items-center h-full">
              <button
                onClick={() => onBreadcrumbClick(breadcrumb.storageRef)}
                className={clsx(
                  'font-semibold text-sm transition-colors text-nowrap rounded-md flex text-left items-center cursor-pointer px-2 py-0.5 h-full',
                  index === breadcrumbs.length - 1 ? 'text-brand-neutral-950' : 'text-brand-neutral-600 rounded-lg transition-colors hover:bg-brand-neutral-100'
                )}
                title={breadcrumb.label}
              >
                {breadcrumb.label}
              </button>
              {index < breadcrumbs.length - 1 && <ChevronRight className="text-brand-neutral-500 w-5 h-5" />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
